/**
 * Change plan (DNKADM-0233) — the Manage-bar action that moves an existing membership
 * to a different plan.
 *
 * **The FE never decides upgrade vs downgrade.** The operator picks a target plan, the
 * dialog posts `{ toPlanId }`, and dink derives the direction from the plan prices and
 * answers with either an immediate charge (a real order/receipt) or a scheduled record
 * carrying the effective/renewal date. Every word of the outcome copy branches on
 * {@link MembershipPlanChange.effect} — the SERVER's signal — never on the local price
 * comparison. The price hint above the picker is explicitly advisory: it never gates
 * submit and never overrides the receipt.
 *
 * Errors are status-first and faithful: a non-2xx is never a success, the dialog stays
 * open, and nothing is claimed about the membership (a `402` declined card charges the
 * card already on the subscription — there is no another-card flow here by design).
 */
import { useCallback, useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ApiError } from '@/api/errors';
import {
  changeMembershipPlan,
  type MembershipOrder,
  type MembershipPlanChange,
} from '@/api/membershipOrders';
import { listMembershipPlans, type MembershipPlan } from '@/api/membershipPlans';
import { serverMessage } from '@/api/serverMessage';
import { declineReasonLineFromError } from '@/utils/declineReason';
import { isNeedsReview } from '@/domain/orderStatus';
import { OrderStatusRow } from '@/components/OrderStatusBadge';
import { scrubBrand } from '@/utils/whitelabel';
import { formatMoney } from '@/utils/v2Format';
import { inkAlpha, MONO, resolveTokens } from '@/theme';

const PLACEHOLDER = '-';

/** Fallback currency when the order/plan carries none (mirrors the Buy receipt). */
const FALLBACK_CURRENCY = 'USD';

interface ChangePlanDialogProps {
  contactId: string;
  /**
   * The plan the membership is on now — excluded from the picker. Read ONCE, when the
   * dialog opens: it is live membership state, and a successful upgrade changes it while
   * the dialog is still showing the receipt for that upgrade (DNKADM-0234).
   */
  currentPlanId: string | null;
  /** Its display name (backend text; scrubbed at the render boundary). */
  currentPlanName: string | null;
  open: boolean;
  onClose: () => void;
  /** Re-fetch membership + credits after the server applied (or scheduled) the change. */
  onChanged: () => void;
}

type PlansState =
  | { status: 'loading' }
  | { status: 'ready'; plans: MembershipPlan[] }
  | { status: 'error'; message: string };

export function ChangePlanDialog({
  contactId,
  currentPlanId,
  currentPlanName,
  open,
  onClose,
  onChanged,
}: ChangePlanDialogProps) {
  if (!open) return null;
  return (
    /* Keyed on the contact ALONE (DNKADM-0234). `currentPlanId` used to be in this key,
       which made an immediate upgrade destroy its own receipt: the post-success refetch
       reports the new plan, the key flips, React remounts the body, and `result` resets
       to `null` — wiping the "charged $X" confirmation on the one path where money
       moved. Per-open reset does not need the plan id: the dialog unmounts on close
       (`if (!open) return null`), so every open mounts a fresh body anyway. */
    <ChangePlanDialogBody
      key={contactId}
      contactId={contactId}
      currentPlanId={currentPlanId}
      currentPlanName={currentPlanName}
      onClose={onClose}
      onChanged={onChanged}
    />
  );
}

function ChangePlanDialogBody({
  contactId,
  currentPlanId,
  currentPlanName,
  onClose,
  onChanged,
}: Omit<ChangePlanDialogProps, 'open'>) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const planFieldId = `${ids}-plan`;

  const [plansState, setPlansState] = useState<PlansState>({ status: 'loading' });
  const [targetPlanId, setTargetPlanId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<MembershipPlanChange | null>(null);
  /**
   * The membership's plan AS OF THIS OPEN (DNKADM-0234). The props are live membership
   * state: an immediate upgrade swaps the plan server-side, and the post-success refetch
   * feeds the NEW plan back in while the receipt is on screen. Reading the props directly
   * would then drop the just-chosen plan out of `choices`, blanking the "New plan" field
   * under the receipt and losing the plan name it falls back to. Snapshotting on mount
   * keeps the form describing the membership the operator actually acted on; each open
   * mounts a new body, so this never goes stale across opens.
   */
  const [openedOn] = useState({ planId: currentPlanId, planName: currentPlanName });

  const loadPlans = useCallback(() => {
    setPlansState({ status: 'loading' });
    void listMembershipPlans({ status: 'active' })
      .then((plans) => setPlansState({ status: 'ready', plans }))
      .catch((error: unknown) =>
        setPlansState({
          status: 'error',
          message: serverMessage(error) ?? 'Membership plans could not be loaded.',
        }),
      );
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const plans = plansState.status === 'ready' ? plansState.plans : [];
  // The current plan is never a target — changing to the plan you are already on is not
  // a change (and the server would reject it).
  const choices = plans.filter((plan) => plan.id !== openedOn.planId);
  const currentPlan = plans.find((plan) => plan.id === openedOn.planId) ?? null;
  const targetPlan = choices.find((plan) => plan.id === targetPlanId) ?? null;
  const hint = advisoryHint(currentPlan, targetPlan);
  const canSubmit =
    plansState.status === 'ready' && targetPlan !== null && !submitting && result === null;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function submit() {
    if (!canSubmit || targetPlan === null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const change = await changeMembershipPlan(contactId, targetPlan.id);
      // A 2xx carrying a FAILED order is NOT a success — say so and change nothing.
      if (change.order.status === 'FAILED') {
        setSubmitError(failedOrderMessage(change.order));
        return;
      }
      setResult(change);
      onChanged();
    } catch (error) {
      setSubmitError(changePlanErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={submitting ? undefined : close}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>Change plan</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick the plan to move this membership to. dink decides whether the change takes
            effect now or at the next renewal, and this dialog shows what it did.
          </Typography>

          <Stack direction="row" sx={{ gap: 1.5, justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Current plan
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
              {planLabel(currentPlan, openedOn.planName)}
            </Typography>
          </Stack>

          {plansState.status === 'error' && (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button color="inherit" size="small" onClick={loadPlans}>
                  Retry
                </Button>
              }
            >
              {plansState.message}
            </Alert>
          )}

          <TextField
            id={planFieldId}
            label="New plan"
            select
            required
            fullWidth
            size="small"
            value={choices.some((plan) => plan.id === targetPlanId) ? targetPlanId : ''}
            onChange={(event) => {
              setSubmitError(null);
              setTargetPlanId(event.target.value);
            }}
            disabled={plansState.status !== 'ready' || submitting || result !== null}
            helperText={
              targetPlan !== null
                ? `${formatMoney(targetPlan.price, FALLBACK_CURRENCY)} · ${targetPlan.termDays} days`
                : ' '
            }
          >
            {plansState.status === 'loading' && (
              <MenuItem value="" disabled>
                Loading plans...
              </MenuItem>
            )}
            {plansState.status === 'ready' && choices.length === 0 && (
              <MenuItem value="" disabled>
                No other membership plan is available
              </MenuItem>
            )}
            {choices.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {scrubBrand(plan.name) ?? PLACEHOLDER} ·{' '}
                {formatMoney(plan.price, FALLBACK_CURRENCY)}
              </MenuItem>
            ))}
          </TextField>

          {hint !== null && result === null && (
            /* `role="note"`, deliberately NOT the MUI Alert default `role="alert"`: this
               is a static advisory guess, not an assertive announcement, and the only
               live regions in this dialog are the real error (`alert`) and the
               authoritative receipt (`status`). */
            <Alert severity="info" icon={false} role="note">
              <Typography sx={{ fontSize: 13 }}>{hint}</Typography>
            </Alert>
          )}

          {submitError !== null && (
            <Alert severity="error" role="alert">
              {submitError}
            </Alert>
          )}

          {result !== null && <ChangeOutcome change={result} targetPlanName={targetPlan?.name ?? null} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting} color="inherit">
          {result !== null ? 'Close' : 'Cancel'}
        </Button>
        {result === null && (
          <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit}>
            {submitting ? 'Changing...' : 'Change plan'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The outcome, branched ONLY on the server's signal:
 *  - `immediate` → the upgrade receipt with the amount charged now — but ONLY once the
 *    order is `COMPLETED`. An immediate change whose order is still `PENDING` has not
 *    necessarily moved any money, so the copy says so instead of claiming a charge (QA
 *    round 1; mirrors `BuyMembershipOrderDialog`'s refusal to call `PENDING` a success).
 *    The success/`onChanged` decision is NOT gated on `COMPLETED` — only this claim is,
 *    because a scheduled downgrade may legitimately carry a non-`COMPLETED` status.
 *  - `scheduled` → the downgrade notice with the effective date and NO charge;
 *  - `unknown` → the receipt with no direction claimed (the response signalled neither).
 */
function ChangeOutcome({
  change,
  targetPlanName,
}: {
  change: MembershipPlanChange;
  targetPlanName: string | null;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const { order, effect, effectiveDate } = change;
  const currency = order.currency ?? FALLBACK_CURRENCY;
  // `tier` is backend text — scrubbed at the render boundary.
  const plan =
    scrubBrand(order.lines[0]?.tier ?? null) ?? scrubBrand(targetPlanName) ?? PLACEHOLDER;
  // Only a COMPLETED order lets us say money actually moved.
  const chargeSettled = order.status === 'COMPLETED';
  const chargedImmediately = effect === 'immediate' && chargeSettled;

  return (
    <Box
      role="status"
      sx={{ border: `1px solid ${t.line}`, borderRadius: 1, p: 1.5, bgcolor: t.surface }}
    >
      <Stack spacing={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {effect === 'immediate'
            ? chargedImmediately
              ? `Upgraded — charged ${formatMoney(order.grandTotal, currency)} now`
              : isNeedsReview(order.status)
                ? 'Payment taken, plan change not applied — needs review'
                : order.status === 'PENDING'
                  ? 'Plan change submitted — nothing has been charged yet'
                  : 'Plan change submitted — check the order before telling the member anything has changed'
            : effect === 'scheduled'
              ? `Downgrade scheduled — takes effect on ${effectiveDate ?? 'the next renewal'}`
              : 'Plan change submitted'}
        </Typography>
        <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>Order</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>
            {order.orderNumber ?? order.orderId ?? PLACEHOLDER}
          </Typography>
        </Stack>
        <OrderStatusRow status={order.status} />
        <Divider />
        <ReceiptRow label="Plan" value={plan} />
        {effect === 'scheduled' ? (
          <ReceiptRow label="Takes effect" value={effectiveDate ?? 'next renewal'} />
        ) : (
          /* "Charged now" is a claim about money that moved — it needs a COMPLETED order.
             An unsettled order shows the same amount under a neutral label instead. */
          <ReceiptRow
            label={chargeSettled ? 'Charged now' : 'Order total'}
            value={formatMoney(order.grandTotal, currency)}
            strong
          />
        )}
        {effect === 'scheduled' && (
          <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
            No charge and no refund — the new plan and its credits start on that date.
          </Typography>
        )}
        {effect !== 'scheduled' && !chargeSettled && (
          <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>
            {isNeedsReview(order.status)
              ? 'The payment was taken but the plan change was not applied. Refund the payment or apply the change yourself before telling the member anything has changed.'
              : order.status === 'PENDING'
                ? 'This order is not settled yet — nothing has been charged. Re-check the order before telling the member anything has changed.'
                : 'Check the order before telling the member anything has changed.'}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function ReceiptRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: inkAlpha(theme, 0.62) }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Stack>
  );
}

/**
 * The ADVISORY price hint — what the prices *suggest*, labelled as a guess. It is never
 * a gate (submit does not consult it) and it is replaced by the receipt the moment the
 * server answers; when either price is unknown there is no hint at all.
 */
function advisoryHint(
  currentPlan: MembershipPlan | null,
  targetPlan: MembershipPlan | null,
): string | null {
  if (currentPlan === null || targetPlan === null) return null;
  if (targetPlan.price > currentPlan.price) {
    return 'This looks like an upgrade: the card on the subscription would be charged the difference now. dink decides — the receipt below is what actually happened.';
  }
  if (targetPlan.price < currentPlan.price) {
    return 'This looks like a downgrade: it would take effect at the next renewal, with no charge or refund. dink decides — the receipt below is what actually happened.';
  }
  return null;
}

function planLabel(plan: MembershipPlan | null, fallbackName: string | null): string {
  if (plan !== null) {
    return `${scrubBrand(plan.name) ?? PLACEHOLDER} · ${formatMoney(plan.price, FALLBACK_CURRENCY)}`;
  }
  return scrubBrand(fallbackName) ?? PLACEHOLDER;
}

function failedOrderMessage(order: MembershipOrder): string {
  const label = order.orderNumber ?? order.orderId;
  const reference = label === null ? '' : ` (order ${label})`;
  return `The plan change failed${reference}. The membership is unchanged.`;
}

/**
 * Status-first, faithful error copy. A `402` is the declined card already on the
 * subscription; `409`/`404`/`403`/`400` surface the server `message`. Every branch
 * appends the `requestId` when the API gave one.
 */
function changePlanErrorMessage(error: unknown): string {
  const fallback = 'The plan change could not be completed. The membership is unchanged.';
  if (!(error instanceof ApiError)) return fallback;
  const message = serverMessage(error);
  const suffix = error.requestId ? ` Request ID: ${error.requestId}` : '';
  if (error.status === 402) {
    // DNKAPI-0676: a known decline reason says why; OTHER/unknown keep the plain line.
    const reasonLine = declineReasonLineFromError(error);
    if (reasonLine !== null) return `${reasonLine} The plan was not changed.${suffix}`;
    return `${message ?? 'The card on this subscription was declined. The plan was not changed.'}${suffix}`;
  }
  return `${message ?? fallback}${suffix}`;
}
